import { useCallback, useMemo, useState } from "react";
import { mergesToRegions, regionsToMergeMap, regionIntersectsRange } from "../utils/mergeUtils";

/**
 * useMerge
 * View-only cell merging, layered on top of the already-displayed merge
 * map (which itself comes from the file, after sort).  State is kept per
 * sheet so switching tabs preserves what the user merged — consistent
 * with freeze / sort / hide-unhide.
 *
 * Returns the effective merge map + (for Merge & Center) an effective
 * style map, plus the toolbar handlers and their enabled/disabled state.
 *
 * Nothing here mutates the workbook: unmerging a region simply drops it,
 * and the cells it had hidden reappear with their original values.
 */
export default function useMerge({ activeSheet, baseMerges, baseStyles, selection }) {
  // sheet -> region[]  (absent key = untouched → use baseMerges as-is)
  const [editsBySheet, setEditsBySheet] = useState({});
  const regions = editsBySheet[activeSheet];

  const r1 = selection ? selection.r1 : 0;
  const c1 = selection ? selection.c1 : 0;
  const r2 = selection ? selection.r2 : 0;
  const c2 = selection ? selection.c2 : 0;

  // Effective merge map fed to the grid.  Untouched sheets pass the base
  // map straight through (identity preserved → no extra render churn).
  const cellMerges = useMemo(
    () => (regions ? regionsToMergeMap(regions) : baseMerges),
    [regions, baseMerges],
  );

  // Merge & Center centres the master cell's content.  Only rebuild the
  // style map when at least one centred merge exists.
  const cellStyles = useMemo(() => {
    if (!regions) return baseStyles;
    const centered = regions.filter((rg) => rg.center);
    if (centered.length === 0) return baseStyles;
    const out = { ...baseStyles };
    for (let i = 0; i < centered.length; i += 1) {
      const rg = centered[i];
      const key = `${rg.r}:${rg.c}`;
      out[key] = { ...(out[key] || {}), textAlign: "center", justifyContent: "center" };
    }
    return out;
  }, [regions, baseStyles]);

  const effectiveRegions = useMemo(
    () => (regions || mergesToRegions(baseMerges)),
    [regions, baseMerges],
  );

  const canMerge = r2 > r1 || c2 > c1;            // a real multi-cell range
  const canUnmerge = useMemo(
    () => effectiveRegions.some((rg) => regionIntersectsRange(rg, r1, c1, r2, c2)),
    [effectiveRegions, r1, c1, r2, c2],
  );

  const applyMerge = useCallback((center) => {
    if (!(r2 > r1 || c2 > c1)) return;
    setEditsBySheet((prev) => {
      const seed = prev[activeSheet] || mergesToRegions(baseMerges);
      // Drop any region overlapping the new selection, then add the merge.
      const kept = seed.filter((rg) => !regionIntersectsRange(rg, r1, c1, r2, c2));
      kept.push({ r: r1, c: c1, rowSpan: r2 - r1 + 1, colSpan: c2 - c1 + 1, center: !!center });
      return { ...prev, [activeSheet]: kept };
    });
  }, [activeSheet, baseMerges, r1, c1, r2, c2]);

  const unmerge = useCallback(() => {
    setEditsBySheet((prev) => {
      const seed = prev[activeSheet] || mergesToRegions(baseMerges);
      const kept = seed.filter((rg) => !regionIntersectsRange(rg, r1, c1, r2, c2));
      return { ...prev, [activeSheet]: kept };
    });
  }, [activeSheet, baseMerges, r1, c1, r2, c2]);

  const mergeCenter = useCallback(() => applyMerge(true), [applyMerge]);
  const mergeCells = useCallback(() => applyMerge(false), [applyMerge]);

  return { cellMerges, cellStyles, canMerge, canUnmerge, mergeCenter, mergeCells, unmerge };
}
